<template>
  <property-report-card
    :title="`${groupingSelection.label} Market Activity`"
    :description="subtitle"
    :printing-mode="printingMode"
    :is-empty="!unitsFound(filteredProperties)"
  >
    <template #actions>
      <q-select
        v-model="groupingSelection"
        transition-duration="100"
        style="width: 150px"
        dense
        outlined
        label="Group By"
        :options="groupingOptions"
        @update:model-value="
          (value) => $tracker.changedFilter('group_by', 'market_activity', value.value)
        "
      />
      <q-select
        v-model="metricSelection"
        transition-duration="100"
        style="width: 150px"
        dense
        outlined
        label="Metric"
        :options="metricOptions"
        @update:model-value="
          (value) => $tracker.changedFilter('metric', 'market_activity', value.value)
        "
      />
      <q-select
        v-model="displaySelection"
        transition-duration="100"
        style="width: 150px"
        dense
        outlined
        label="Display"
        :options="displayOptions"
        @update:model-value="
          (value) => $tracker.changedFilter('display', 'market_activity', value.value)
        "
      />
    </template>
    <q-separator />
    <q-card-section>
      <div id="intercom-weekly-market-activity-content" style="overflow-x: auto">
        <div style="width: 100%; height: 300px">
          <bar class="chart" :data="chartData" :options="chartOptions" :plugins="plugins" />
        </div>
      </div>
      <q-card flat>
        <div class="flex justify-between q-mb-md">
          <div class="text-bold row items-center q-gutter-sm">
            <div>{{ metricSelection.label }} By Property</div>
          </div>
          <div class="q-gutter-sm"></div>
          <q-btn flat no-caps class="q-my-auto" @click="exportActivityGrid" v-if="!printingMode">
            <q-icon name="download" class="q-mr-sm" size="medium" />Download
          </q-btn>
        </div>
        <div>
          <q-table
            :rows="activityGridRows"
            :columns="activityGridColumns"
            :rows-per-page-options="[0]"
            hide-bottom
            dense
            flat
            bordered
            row-key="id"
            :table-header-class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'"
          >
            <template #body="props">
              <q-tr
                :props="props"
                :class="{
                  'text-bold bg-deep-purple-light text-primary':
                    subjectDetail && props.row.id === subjectDetail.id
                }"
              >
                <q-td
                  v-for="col in props.cols"
                  :key="col.name"
                  :props="props"
                  :style="
                    col.field === 'average' ? 'border-left: 1px rgba(0, 0, 0, 0.12) solid' : ''
                  "
                >
                  <div v-if="col.name === 'label'" :class="{ 'constrained-cell': col.name === '' }">
                    {{ col.value }}
                  </div>
                  <a
                    v-else-if="col.field !== 'total' && col.field !== 'average'"
                    class="text-primary"
                    href="#"
                    @click="
                      userSelection = {
                        week: col.name,
                        beds: null,
                        propId: props.row.id
                      }
                    "
                  >
                    <span v-if="displaySelection.value === 'annual_percent'">
                      {{
                        annualUnitsCount > 0 && col.value.length
                          ? formatPercent(col.value.length / annualUnitsCount) || ''
                          : ''
                      }}
                    </span>
                    <span v-else>
                      {{ col.value.length || '' }}
                    </span>
                  </a>
                  <div v-else class="text-bold">
                    {{ col.value }}
                  </div>
                </q-td>
              </q-tr>
              <template v-if="props.rowIndex === activityGridRows.length - 1">
                <q-tr
                  v-for="(footer, index) in [footerAvgRow, footerTotalRow]"
                  :key="index"
                  class="text-bold text-center title-row"
                  :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'"
                >
                  <q-td class="constrained-cell text-left">
                    <div>
                      Comp {{ index === 0 ? 'Average' : 'Total' }}
                      <q-icon v-if="subjectDetail" name="info_outline">
                        <q-tooltip>
                          Computed with underlying units (Does not include
                          {{ subjectDetail.building_name || subjectDetail.street_address }})
                        </q-tooltip>
                      </q-icon>
                    </div>
                  </q-td>
                  <q-td
                    v-for="(val, i) in footer"
                    :key="i"
                    class="text-center"
                    :style="
                      i === footer.length - 2 ? 'border-left: 1px rgba(0, 0, 0, 0.12) solid' : ''
                    "
                  >
                    {{ val }}
                  </q-td>
                </q-tr>
              </template>
            </template>
          </q-table>
        </div>
      </q-card>
    </q-card-section>
    <q-separator v-if="!printingMode" />
    <q-card-section v-if="!printingMode">
      <q-card flat>
        <div
          v-if="userSelection && selectedRange"
          class="flex justify-between q-mb-md"
          id="selected-units-table"
        >
          <div class="text-bold q-mb-md q-my-auto">
            {{ metricSelection.label }}
            <span v-if="selectedRange.from === selectedRange.to"> for {{ selectedRange.to }} </span>
            <span v-else>
              Between {{ selectedRange.from }} and
              {{ selectedRange.to }}
            </span>
            <q-icon name="close" class="q-ml-md cursor-pointer" @click="closePanel" />
          </div>
          <q-btn flat no-caps class="q-my-auto" @click="exportCSV">
            <q-icon name="download" class="q-mr-sm" size="medium" />Download
          </q-btn>
        </div>
        <div
          v-if="!tableUnits.length"
          class="full-width text-center justify-center flex align-center"
        >
          <q-icon name="info" size="18px" color="accent" class="q-mr-sm q-my-auto" />
          Click on a bar in the chart above to see the units that make it up.
        </div>
        <unit-details-table
          v-else
          :rows="tableUnits"
          :expanded-unit-history="expandedUnitHistory"
        />
      </q-card>
    </q-card-section>
  </property-report-card>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import {
  addDays,
  addMonths,
  eachDayOfInterval,
  eachWeekOfInterval,
  eachMonthOfInterval,
  format,
  isAfter,
  isBefore,
  parseISO,
  type Interval,
  eachQuarterOfInterval,
  endOfQuarter
} from 'date-fns'
import { enUS } from 'date-fns/locale/en-US'
import { Bar } from 'vue-chartjs'
import { type ChartOptions } from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import {
  type PropertyDetailsResponse,
  type Availability,
  type Price,
  type AvailabilityPeriod,
  getLatestPricesByFilter,
  UnitAmenityTag
} from 'common'
import { getUnitsByFilter } from 'common'
import UnitDetailsTable, { exportCSV } from '@/components/PropertyReports/UnitDetailsTable.vue'
import PropertyReportCard from '@/components/PropertyReportCard.vue'
import { formatNumber, formatPercent, unitsFound, unitTypeColor } from '@/utils'
import { exportFile } from 'quasar'

type ActivityRow = {
  [key: string]:
    | string
    | {
        unit: Availability
        period: AvailabilityPeriod
        prop: PropertyDetailsResponse
      }[]
}

export default {
  name: 'PropertyCompareVolumeOverTime',
  components: { PropertyReportCard, Bar, UnitDetailsTable },
  props: {
    subjectDetail: {
      type: Object as PropType<PropertyDetailsResponse | null>,
      default: null
    },
    filteredProperties: {
      type: Array<{
        prop: PropertyDetailsResponse
        units: {
          unit: Availability
          lastHistory: Price
          period: AvailabilityPeriod
        }[]
      }>,
      required: true
    },
    expandedUnitHistory: {
      type: Object as PropType<{
        [unit_id: string]: {
          created_on: string
          history: Price
        }[]
      }>,
      required: true
    },
    listingsSelectionValue: {
      type: String as PropType<'all' | 'active' | 'closed'>,
      required: true
    },
    dateRange: {
      type: Object as PropType<{ from: Date; to: Date }>,
      required: true
    },
    printingMode: {
      type: Boolean,
      default: false
    },
    sqftRange: {
      type: Object as PropType<{ min: number | null; max: number | null }>,
      required: true
    },
    unitTypes: {
      type: Array as PropType<{ bed: number; bath: number; partial_bath: number | null }[]>,
      required: true
    },
    includedAmenities: {
      type: Array as PropType<UnitAmenityTag[]>,
      required: true
    },
    excludedAmenities: {
      type: Array as PropType<UnitAmenityTag[]>,
      required: true
    },
    leaseTerms: {
      type: Set<number | null>,
      required: true
    }
  },
  data() {
    return {
      plugins: [ChartDataLabels],
      userSelection: null as {
        week: string
        beds: number | null
        propId: string | null
      } | null,
      metricSelection: { label: 'Leased Units', value: 'leased' },
      metricOptions: [
        { label: 'New Units', value: 'available' },
        { label: 'Leased Units', value: 'leased' }
      ],
      groupingSelection: { label: 'Weekly', value: 'weekly', suffix: 'week' },
      groupingOptions: [
        { label: 'Daily', value: 'daily', suffix: 'day' },
        { label: 'Day of the Week', value: 'dayOfWeek', suffix: 'day of the week' },
        { label: 'Weekly', value: 'weekly', suffix: 'week' },
        { label: 'Monthly', value: 'monthly', suffix: 'month' },
        { label: 'Quarterly', value: 'quarterly', suffix: 'quarter' }
      ],
      displaySelection: { label: 'Count', value: 'count' },
      displayOptions: [
        { label: 'Count', value: 'count' },
        { label: 'Annual Percent', value: 'annual_percent' }
      ]
    }
  },
  computed: {
    annualUnitsCount(): number {
      const dateRangeToMinusOneYear = addDays(this.dateRange.to, -365)
      const units = this.filteredProperties.flatMap((_) =>
        getUnitsByFilter(
          _.prop,
          getLatestPricesByFilter(
            _.prop,
            dateRangeToMinusOneYear,
            this.dateRange.to,
            this.sqftRange.min,
            this.sqftRange.max,
            this.unitTypes,
            this.includedAmenities,
            this.excludedAmenities
          ),
          this.dateRange.to,
          this.listingsSelectionValue
        ).filter(({ lastHistory }) => {
          return (
            !this.leaseTerms.size ||
            (this.leaseTerms.has(null) && !lastHistory.price_plans?.length) ||
            lastHistory.price_plans?.some((p) => this.leaseTerms.has(p[0]))
          )
        })
      )
      if (this.metricSelection.value === 'leased') {
        return units.filter(({ period }) =>
          this.isMarketDateInRange(period.exit_market, dateRangeToMinusOneYear, this.dateRange.to)
        ).length
      } else {
        return units.filter(({ period }) =>
          this.isMarketDateInRange(period.enter_market, dateRangeToMinusOneYear, this.dateRange.to)
        ).length
      }
    },
    activityGridColumns() {
      return [{ name: 'label', label: '', field: 'label', align: 'left' }].concat(
        ...this.generateData
          .map((value) => ({
            name: value.week,
            label: this.formatLabel(value.week),
            field: value.week,
            align: 'center'
          }))
          .concat(
            ...[
              { name: 'average', label: 'Avg', field: 'average', align: 'center' },
              { name: 'total', label: 'Total', field: 'total', align: 'center' }
            ]
          )
      )
    },
    activityGridRows() {
      const rows: ActivityRow[] = []
      for (const { prop } of this.filteredProperties) {
        let total = 0
        const row = {
          id: prop.id,
          label: prop.building_name || prop.street_address
        } as ActivityRow

        for (const value of this.generateData) {
          const units = value.units.filter((_) => _.prop.id === prop.id)
          row[value.week] = units
          const count = units.length
          if (this.displaySelection.value === 'annual_percent') {
            total += this.annualUnitsCount > 0 ? count / this.annualUnitsCount : 0
          } else {
            total += count
          }
        }

        if (this.displaySelection.value === 'annual_percent') {
          row.total = formatPercent(total)
          row.average = formatPercent(total / this.generateData.length)
        } else {
          row.total = formatNumber(total)
          row.average = formatNumber(total / this.generateData.length, false, 1)
        }
        rows.push(row)
      }
      return rows
    },
    footerAvgRow(): (string | null)[] {
      let total = 0
      const compIds = new Set(
        this.filteredProperties
          .map(({ prop }) => prop.id)
          .filter((_) => _ !== this.subjectDetail?.id)
      )
      return this.generateData
        .map((value) => {
          const units = value.units.filter((_) => _.prop.id !== this.subjectDetail?.id)
          const propIds = new Set(units.map((_) => _.prop.id))
          const count = units.length
          if (this.displaySelection.value === 'annual_percent') {
            total += this.annualUnitsCount > 0 ? count / this.annualUnitsCount : 0
          } else {
            total += count
          }
          if (count) {
            if (this.displaySelection.value === 'annual_percent') {
              return this.annualUnitsCount > 0
                ? formatPercent(count / (this.annualUnitsCount * propIds.size))
                : '0%'
            }
            return formatNumber(count / propIds.size, false, 1)
          }
          return ''
        })
        .concat([
          total
            ? this.displaySelection.value === 'annual_percent'
              ? formatPercent(total / (this.generateData.length * compIds.size))
              : formatNumber(total / this.generateData.length / compIds.size, false, 1)
            : '',
          total
            ? this.displaySelection.value === 'annual_percent'
              ? formatPercent(total / compIds.size)
              : formatNumber(total / compIds.size)
            : ''
        ])
    },
    footerTotalRow(): (string | null)[] {
      let total = 0
      return this.generateData
        .map((value) => {
          const units = value.units.filter((_) => _.prop.id !== this.subjectDetail?.id)
          const count = units.length
          if (this.displaySelection.value === 'annual_percent') {
            total += this.annualUnitsCount > 0 ? count / this.annualUnitsCount : 0
          } else {
            total += count
          }
          if (count) {
            if (this.displaySelection.value === 'annual_percent') {
              return this.annualUnitsCount > 0 ? formatPercent(count / this.annualUnitsCount) : '0%'
            }
            return formatNumber(count)
          }
          return ''
        })
        .concat([
          total
            ? this.displaySelection.value === 'annual_percent'
              ? formatPercent(total / this.generateData.length)
              : formatNumber(total / this.generateData.length, false, 1)
            : '',
          total
            ? this.displaySelection.value === 'annual_percent'
              ? formatPercent(total)
              : formatNumber(total)
            : ''
        ])
    },
    selectedRange() {
      if (this.userSelection === null) {
        return null
      }

      const date = parseISO(this.userSelection.week)
      let formattedFrom: string
      let formattedTo: string

      switch (this.groupingSelection.value) {
        case 'daily':
          formattedFrom = format(date, 'M/d/yy')
          formattedTo = formattedFrom
          break
        case 'monthly':
          formattedFrom = format(date, 'M/d/yy')
          formattedTo = format(addMonths(date, 1), 'M/d/yy')
          break
        case 'dayOfWeek':
          formattedFrom = format(date, 'EEEE') + 's'
          formattedTo = formattedFrom
          break
        case 'weekly':
        default:
          formattedFrom = format(date, 'M/d/yy')
          formattedTo = format(addDays(date, 6), 'M/d/yy')
      }

      this.$tracker.clickedOnGraph('market_activity')

      return {
        from: formattedFrom,
        to: formattedTo
      }
    },
    generateData() {
      if (this.filteredProperties.length === 0) {
        return []
      }

      const intervalStartMaps = {
        daily: eachDayOfInterval,
        monthly: eachMonthOfInterval,
        weekly: (interval: Interval) => eachWeekOfInterval(interval, { weekStartsOn: 1 }),
        quarterly: eachQuarterOfInterval,
        dayOfWeek: (interval: Interval) => {
          // get the first 7 days of the day range to represent each day of the week's interval start
          const days = eachDayOfInterval({
            start: interval.start,
            end: addDays(interval.start, 6)
          })

          // sort by day of week (0 = Sunday, 1 = Monday, etc.), this way they appear on the chart in order
          days.sort((a, b) => a.getDay() - b.getDay())

          return days
        }
      }

      const getIntervalStartFn =
        intervalStartMaps[this.groupingSelection.value] || eachWeekOfInterval

      return getIntervalStartFn(
        {
          start: this.dateRange.from,
          end: this.dateRange.to
        },
        {
          locale: enUS
        }
      ).map((start) => {
        const getIntervalEnd = (start: Date) => {
          switch (this.groupingSelection.value) {
            case 'daily':
              return start
            case 'monthly':
              return addMonths(start, 1)
            case 'quarterly':
              return endOfQuarter(start)
            case 'dayOfWeek':
              // Get the day of week from the start date (0=Sunday, 1=Monday, etc.)
              const targetDay = start.getDay()
              const lastDay = new Date(this.dateRange.to)

              // Calculate how many days to subtract to get to the target day of week
              // If lastDay is Wednesday (3) and targetDay is Monday (1), we need to go back 2 days
              // If lastDay is Monday (1) and targetDay is Wednesday (3), we need to go back 5 days
              let daysToSubtract = lastDay.getDay() - targetDay
              if (daysToSubtract < 0) {
                daysToSubtract += 7
              }

              lastDay.setDate(lastDay.getDate() - daysToSubtract)

              return lastDay
            default:
              // weekly
              return addDays(start, 7)
          }
        }
        const end = getIntervalEnd(start)

        // constrain start and end dates to dateRange
        const constrainedEnd = isAfter(end, this.dateRange.to) ? this.dateRange.to : end
        const constrainedStart = isBefore(start, this.dateRange.from) ? this.dateRange.from : start

        const formattedIntervalStart = format(start, 'yyyy-MM-dd')
        const weekUnits = this.filteredProperties.flatMap(({ prop, units }) => {
          return getUnitsByFilter(prop, units, constrainedEnd, this.listingsSelectionValue).map(
            (_) => ({
              unit: _.unit,
              lastHistory: _.lastHistory,
              period: _.period,
              prop
            })
          )
        })

        let units: {
          unit: Availability
          lastHistory: Price
          period: AvailabilityPeriod
          prop: PropertyDetailsResponse
        }[] = []
        if (this.metricSelection.value === 'leased') {
          units = weekUnits.filter(({ period }) =>
            this.isMarketDateInRange(period.exit_market, constrainedStart, constrainedEnd)
          )
        } else {
          units = weekUnits.filter(({ period }) =>
            this.isMarketDateInRange(period.enter_market, constrainedStart, constrainedEnd)
          )
        }

        return { week: formattedIntervalStart, units }
      })
    },
    relevantBeds() {
      const beds = [
        ...new Set(
          this.filteredProperties.flatMap(({ units }) => units.map(({ unit }) => unit.bed))
        )
      ]
      beds.sort()
      return beds as number[]
    },
    chartData() {
      const selectedWeek = this.userSelection?.week ?? ''
      const selectedBeds = this.userSelection?.beds ?? -1
      const datasets: {
        label: string
        borderColor: string
        backgroundColor: (ctx: { dataIndex: number; datasetIndex: number }) => string
        data: number[]
        borderWidth: number
        datalabels: {
          align: string
          anchor: string
          font: { weight: string }
          formatter: (value: string) => string
        }
      }[] = []
      this.relevantBeds.forEach((bed) => {
        const dataset = {
          label: `${bed}BR`,
          borderColor: unitTypeColor(bed),
          backgroundColor: (ctx: { dataIndex: number; datasetIndex: number }) => {
            if (
              selectedWeek === this.generateData[ctx.dataIndex].week &&
              ctx.datasetIndex === this.relevantBeds.indexOf(selectedBeds)
            ) {
              return unitTypeColor(bed)
            }
            return unitTypeColor(bed, 0.6)
          },
          data: this.generateData.map((col) => {
            const count = col.units.filter((_) => _.unit.bed === bed).length
            if (this.displaySelection.value === 'annual_percent') {
              return this.annualUnitsCount > 0 ? count / this.annualUnitsCount : 0
            }
            return count
          }),
          borderWidth: 1,
          borderRadius: 4,
          barPercentage: 0.9,
          maxBarThickness: 150,
          datalabels: {
            align: 'center',
            anchor: 'center',
            font: { weight: 'bold' },
            color: this.$q.dark.isActive ? 'white' : undefined,
            formatter: (val) =>
              val
                ? this.displaySelection.value === 'annual_percent'
                  ? formatPercent(val)
                  : formatNumber(val)
                : ''
          }
        }
        datasets.push(dataset)
      })
      if (datasets.length) {
        datasets[datasets.length - 1].datalabels.align = 'top'
        datasets[datasets.length - 1].datalabels.anchor = 'end'
      }
      return {
        labels: this.generateData.map((data) => this.formatLabel(data.week)),
        datasets
      }
    },
    tableUnits() {
      if (this.userSelection?.week) {
        return this.generateData
          .find((_) => _.week === this.userSelection?.week)!
          .units.filter(({ prop, unit }) => {
            let pass = true
            if (this.userSelection?.beds != null) {
              pass = pass && this.userSelection.beds === unit.bed
            }
            if (this.userSelection?.propId != null) {
              pass = pass && this.userSelection.propId === prop.id
            }
            return pass
          })
      }
      return []
    },

    chartOptions() {
      return {
        onClick: (_, clickedElements) => {
          if (this.printingMode) {
            return
          }
          if (clickedElements.length === 0) {
            return
          }
          const { dataIndex, datasetIndex } = clickedElements[0].element.$context
          this.userSelection = {
            week: this.generateData[dataIndex].week,
            beds: this.relevantBeds[datasetIndex],
            propId: null
          }
        },
        maintainAspectRatio: false,
        animation: false,
        plugins: {
          legend: {
            onClick: () => null,
            display: true,
            position: 'top',
            labels: {
              boxWidth: 10,
              boxHeight: 10,
              font: {
                size: 12
              }
            }
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const label = context.dataset.label || ''
                const value = context.parsed.y
                return `${label}: ${this.displaySelection.value === 'annual_percent' ? formatPercent(value) : formatNumber(value)}`
              }
            }
          }
        },
        options: {
          scales: {
            x: {
              type: 'time',
              time: {
                unit: 'week'
              },
              min: this.dateRange.from.valueOf(),
              max: this.dateRange.to.valueOf()
            }
          }
        },
        scales: {
          x: {
            stacked: true,
            ticks: {
              callback: (_, index) => this.chartData.labels[index]
            }
          },
          y: {
            stacked: true,
            grace: '25%'
          }
        }
      } as ChartOptions
    },
    subtitle() {
      const metric = this.groupingSelection.label.toLowerCase()

      return `Track ${metric} lease activity and drill into individual transactions.`
    }
  },
  watch: {
    dateRange() {
      this.userSelection = null
      // Update grouping when date range changes
      this.groupingSelection = this.getGroupingValue()
    },
    groupingSelection() {
      this.userSelection = null
    }
  },
  mounted() {
    this.groupingSelection = this.getGroupingValue()
  },
  methods: {
    unitsFound,
    formatPercent,
    formatNumber,
    closePanel() {
      this.userSelection = null
    },
    exportCSV() {
      exportCSV(this.tableUnits, 'market-activity')
      this.$tracker.exported('market_activity')
    },
    formatLabel(date: string) {
      const fromDate = parseISO(date)
      let toDate: Date
      let formatStr: string

      switch (this.groupingSelection.value) {
        case 'daily':
          return format(fromDate, 'M/d')
        case 'monthly':
          formatStr = format(fromDate, 'MMM yyyy')
          toDate = addMonths(fromDate, 1)
          break
        case 'quarterly':
          formatStr = `Q${Math.floor(fromDate.getMonth() / 3) + 1} ${format(fromDate, 'yyyy')}`
          toDate = addMonths(fromDate, 3)
          break
        case 'dayOfWeek':
          return format(fromDate, 'EEEE')
        default:
          // weekly
          toDate = addDays(fromDate, 6)
          formatStr = `${format(fromDate, 'M/d')} - ${format(toDate, 'M/d')}`
      }

      if (isBefore(fromDate, this.dateRange.from) || isAfter(toDate, this.dateRange.to)) {
        return `${formatStr}*`
      }

      return formatStr
    },
    exportActivityGrid() {
      const rows = this.activityGridRows.map((row) => {
        const exportRow = {} as { [key: string]: string }
        for (const col of this.activityGridColumns) {
          if (!col.label) {
            exportRow['Property Name'] = row[col.field].toString()
          } else if (col.field !== 'average' && col.field !== 'total') {
            if (this.displaySelection.value === 'annual_percent') {
              exportRow[col.label] =
                this.annualUnitsCount > 0
                  ? formatPercent(row[col.field].length / this.annualUnitsCount, 3)
                  : '0%'
            } else {
              exportRow[col.label] = row[col.field].length.toString()
            }
          } else {
            exportRow[col.label] = row[col.field].toString()
          }
        }
        return exportRow
      })
      const today = new Date().toISOString().split('T')[0]
      exportFile(
        'hello-data-market-activity-grid-' + today + '.csv',
        `${Object.keys(rows[0])}\n` + rows.map((row) => Object.values(row).join(',')).join('\n')
      )
      this.$tracker.exported('market_activity_grid')
    },
    getGroupingValue() {
      const msInDay = 24 * 60 * 60 * 1000
      const days = (this.dateRange.to.getTime() - this.dateRange.from.getTime()) / msInDay

      if (days <= 7) {
        return { label: 'Daily', value: 'daily', suffix: 'day' }
      } else if (days <= 180) {
        return { label: 'Weekly', value: 'weekly', suffix: 'week' }
      } else if (days <= 365) {
        return { label: 'Monthly', value: 'monthly', suffix: 'month' }
      } else {
        return { label: 'Quarterly', value: 'quarterly', suffix: 'quarter' }
      }
    },
    isMarketDateInRange(marketDate: string, startDate: Date, endDate: Date) {
      if (!marketDate) return false

      const marketDateObj = parseISO(marketDate)

      if (this.groupingSelection.value === 'dayOfWeek') {
        // For day of week, check if the market date falls on the same day of week
        return marketDateObj.getDay() === startDate.getDay()
      }

      const start = format(startDate, 'yyyy-MM-dd')
      const end = format(endDate, 'yyyy-MM-dd')

      if (this.groupingSelection.value === 'daily') {
        return marketDate >= start && marketDate <= end
      }

      return marketDate >= start && marketDate < end
    }
  }
}
</script>
<style scoped>
.chart {
  height: 450px;
  width: 100%;
}

@media print {
  a {
    text-decoration: none;
  }
}
</style>
